import { useAccommodationPlaceV2ByIdQuery } from '../AccommodationPlacePartner/AccommodationPlacePartner.graphql.generated';
import { useHistory } from '@shared/core';
import { Flex } from '@withjoy/joykit';
import React, { useEffect, useState } from 'react';
import { useMount } from '@shared/utils/hooks/useMount';
import { useHotelBookingGuestSiteTelemetry } from '../../HotelBooking.telemetry';
import { getValidBookingLink } from '../../HotelBooking.utils';
import { JoyLogoLoader } from '@shared/components/JoyLogoLoader';
import { RedirectWithSafeLink } from '@apps/guest/components/RedirectWithSafeLink';
import { useHotelBookingRedirectHealth, wrapWithHotelBookingRedirectProvider } from '../../HotelBooking.health';
import { useUtmParameters } from '@shared/utils/utmParameters';

export const RoomBlockHotel = wrapWithHotelBookingRedirectProvider(({ roomBlockHotelId }: { roomBlockHotelId: string }) => {
  const { onLoadFailure, onLoadComplete } = useHotelBookingRedirectHealth();
  const {
    data: accommodationsPlaceV2Data,
    loading: isAccommodationsPlaceV2Loading,
    error: accommodationsPlaceV2Error,
  } = useAccommodationPlaceV2ByIdQuery({
    variables: {
      accommodationPlaceV2ByIdId: roomBlockHotelId,
    },
    batchMode: 'off',
    skip: !roomBlockHotelId,
  });
  const history = useHistory();

  const [safeLink, setSafeLink] = useState<string | undefined>();

  const telemetry = useHotelBookingGuestSiteTelemetry();
  const utmParams = useUtmParameters();

  useMount(() => {
    telemetry.roomblockhotel.enter();
  });

  useEffect(() => {
    if (isAccommodationsPlaceV2Loading) return;

    if (accommodationsPlaceV2Error) {
      telemetry.guestRoomblockHotelQueryError(() => {
        onLoadFailure(`RoomBlockHotel: useAccommodationPlaceV2ByIdQuery`, accommodationsPlaceV2Error, { forceErrorUI: true });
      }, utmParams);
      return;
    }

    if (accommodationsPlaceV2Data) {
      const bookingLink = accommodationsPlaceV2Data?.accommodationPlaceV2ById?.accommodationRoomBlock?.bookingUrl || '';
      const validBookingLink = getValidBookingLink(bookingLink);
      if (!validBookingLink) {
        telemetry.guestRoomblockHotelRedirect(
          bookingLink,
          bookingLink,
          false,
          () => {
            onLoadFailure(`RoomBlockHotel: Invalid Booking Link`, undefined, { forceErrorUI: true });
          },
          utmParams,
        );
        return;
      }
      telemetry.guestRoomblockHotelRedirect(
        bookingLink,
        validBookingLink.hostname,
        true,
        () => {
          onLoadComplete();
          setSafeLink(validBookingLink.toString());
        },
        utmParams,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accommodationsPlaceV2Data, accommodationsPlaceV2Error, history, utmParams]);

  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      height="100vh"
      width="100vw"
    >
      {isAccommodationsPlaceV2Loading && <JoyLogoLoader loaderKey="guest-hotel-booking-room-block" />}
      <RedirectWithSafeLink
        href={safeLink}
        skipTelemetry
        showEmptyTabMessage
        target="_self"
        onClose={() => {
          setSafeLink(undefined);
        }}
      />
    </Flex>
  );
});
